import { UploadCloudIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { ConflictDialogHost } from '../dialogs/ConflictDialog';

import { UploadPanel } from './UploadPanel';
import { uploadSummary, useUploadStore } from './uploadStore';

/** Ring around the upload icon showing how much of the running uploads went through. */
function ProgressRing({ percent }: { percent: number }) {
  const radius = 15;
  const length = 2 * Math.PI * radius;
  return (
    <svg
      viewBox="0 0 36 36"
      className="pointer-events-none absolute inset-0 size-full -rotate-90"
      aria-hidden
    >
      <circle cx="18" cy="18" r={radius} fill="none" strokeWidth="2.5" className="stroke-border" />
      <circle
        cx="18"
        cy="18"
        r={radius}
        fill="none"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={length}
        strokeDashoffset={length * (1 - percent / 100)}
        className="stroke-brand transition-[stroke-dashoffset] duration-300"
      />
    </svg>
  );
}

/**
 * The disk's app-wide layer, mounted in the top bar so it lives on every page: a button with the progress of running
 * uploads (shown only while there are uploads and their panel is closed), the uploads panel and the conflict dialog.
 */
export function DiskTopBarWidget() {
  const { t } = useTranslation('disk');
  const tasks = useUploadStore((s) => s.tasks);
  const open = useUploadStore((s) => s.open);
  const setOpen = useUploadStore((s) => s.setOpen);
  const summary = uploadSummary(tasks);
  const label = t('upload.uploading', { count: summary.active });

  return (
    <>
      {!open && summary.active > 0 && (
        <SimpleTooltip content={`${label} · ${summary.percent}%`}>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setOpen(true)}
            aria-label={label}
            data-testid="disk-uploads-button"
          >
            <ProgressRing percent={summary.percent} />
            <UploadCloudIcon className="size-4" />
          </Button>
        </SimpleTooltip>
      )}
      <UploadPanel />
      <ConflictDialogHost />
    </>
  );
}
